import type { FilingStatus, TaxFilingReadinessPayload } from '../api/tax-filing';

export type TaxFilingTruthStage = 'not_filed' | FilingStatus;

function getReadyToExportHeadline(readiness: TaxFilingReadinessPayload): string {
  return `Ready to export for ${readiness.year}`;
}

function getMissingExportProofHeadline(readiness: TaxFilingReadinessPayload): string {
  return `Missing export proof for ${readiness.year}`;
}

function isTaxFilingTruthStage(value: unknown): value is TaxFilingTruthStage {
  return value === 'not_filed'
    || value === 'pending'
    || value === 'submitted'
    || value === 'accepted'
    || value === 'rejected';
}

export function getTaxFilingTruthStage(readiness: TaxFilingReadinessPayload): TaxFilingTruthStage {
  if (isTaxFilingTruthStage(readiness.filingTruth?.stage)) {
    return readiness.filingTruth.stage;
  }

  if (readiness.proof.filing.status) {
    return readiness.proof.filing.status;
  }

  if (readiness.verdict === 'already_filed') {
    return 'accepted';
  }

  return 'not_filed';
}

export function getTaxFilingTruthChipLabel(readiness: TaxFilingReadinessPayload): string {
  switch (getTaxFilingTruthStage(readiness)) {
    case 'pending':
      return 'Pending';
    case 'submitted':
      return 'Submitted';
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Not filed';
  }
}

export function getTaxFilingOwnerHeadline(readiness: TaxFilingReadinessPayload): string {
  switch (getTaxFilingTruthStage(readiness)) {
    case 'pending':
      return `Filing in progress for ${readiness.year}`;
    case 'submitted':
      return `Submitted — awaiting acceptance for ${readiness.year}`;
    case 'accepted':
      return `Already filed for ${readiness.year}`;
    case 'rejected':
      return `Rejected — action required for ${readiness.year}`;
    default:
      break;
  }

  if (readiness.verdict === 'blocked') {
    switch (readiness.blockerCode) {
      case 'missing_lots':
        return `Missing lots for ${readiness.year}`;
      case 'no_stored_report':
      case 'no_export_proof':
        return getMissingExportProofHeadline(readiness);
      default:
        return `Blocked for ${readiness.year}`;
    }
  }

  return getReadyToExportHeadline(readiness);
}

export function getTaxFilingSubordinateHeadline(readiness: TaxFilingReadinessPayload): string {
  switch (getTaxFilingTruthStage(readiness)) {
    case 'pending':
      return `Filing in progress for ${readiness.year}`;
    case 'submitted':
      return `Submitted — awaiting acceptance for ${readiness.year}`;
    case 'accepted':
      return `Accepted filing for ${readiness.year}`;
    case 'rejected':
      return `Rejected — action required for ${readiness.year}`;
    default:
      return getTaxFilingOwnerHeadline(readiness);
  }
}
